import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faSpinner,
  faSquare,
  faSquareCheck,
  faChessKing,
  faCircleCheck,
  faCircleHalfStroke,
} from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../store';
import { toggleGameSelected } from '../reducers/uiReducer';
import { setParams } from '../reducers/paramsReducer';
import AppContext from '../context/AppContext';
import GamesAPI from '../api/GamesAPI';
import { GameModel } from '../types/GamesModel';
import { Platform } from '../types/Platform';
import { fixUrl } from './FixContainer';
import { speedLook } from './GamesFiltersTimeControls';

const PAGE_SIZE = 20;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// "Today", "Yesterday", "Tuesday", "Aug 1", "Aug 1, 2025".
function dayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// The time control as a pill coloured by speed, so bullet and rapid games tell apart at a glance.
function TimeControlPill({ label }: { label: string }): React.ReactNode {
  const { speed, icon, cls } = speedLook(label);
  return (
    <span className={`cm-glist-tc ${cls}`} title={speed ?? undefined}>
      <FontAwesomeIcon icon={icon} />
      {label}
    </span>
  );
}

// Where the game stands: importing, being evaluated, not evaluated, or evaluated with how many of
// your mistakes are fixed.
function GameStatus({ game }: { game: GameModel }): React.ReactNode {
  if (game.MoveCount !== game.MoveTotal) {
    return (
      <span className="cm-gstatus is-busy">
        <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
        Importing
      </span>
    );
  }
  if (game.LastEvaluation == null) {
    return game.Evaluating ? (
      <span className="cm-gstatus is-busy">
        <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
        Evaluating
      </span>
    ) : (
      <span className="cm-gstatus is-pending">
        <FontAwesomeIcon icon={faCircleHalfStroke} className="me-1" />
        Not evaluated
      </span>
    );
  }
  const mistakes = game.RepairMistakes;
  const fixed = game.RepairedCount ?? 0;
  const done = mistakes != null && fixed >= mistakes;
  return (
    <span className={`cm-gstatus is-done${done ? ' is-all-fixed' : ''}`}>
      <FontAwesomeIcon icon={faCircleCheck} className="me-1" />
      {mistakes == null
        ? 'Evaluated'
        : mistakes === 0
          ? 'No mistakes'
          : `${fixed} of ${mistakes} fixed`}
    </span>
  );
}

export default function GamesTable(): React.ReactNode {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AppContext);
  const { games } = useAppSelector((state) => state.gamesReducer);
  const { params } = useAppSelector((state) => state.paramsReducer);
  const page = params.page ?? 0;
  const { selectionMode, selectedGameIds } = useAppSelector((state) => state.uiReducer);

  // Starring updates at once, without refetching the page.
  const [starOverrides, setStarOverrides] = useState<Record<number, boolean>>({});
  useEffect(() => {
    setStarOverrides({});
  }, [games]);

  if (games == null) {
    return null;
  }

  const filteredUser = params.username ? params.username.toLowerCase() : null;
  const open = (game: GameModel) => {
    if (selectionMode) {
      dispatch(toggleGameSelected(game.Id));
    } else if (game.MoveCount === game.MoveTotal) {
      navigate(fixUrl(game.Id, params));
    }
  };

  return (
    <div className="cm-games">
      {games.List.map((game) => {
        const ready = game.MoveCount === game.MoveTotal;
        const starred = starOverrides[game.Id] ?? game.Starred;
        const selected = selectedGameIds.includes(game.Id);
        const side = (name: string, elo: string, won: boolean) => (
          <span className="cm-side">
            <span
              className={`cm-name${name.toLowerCase() === filteredUser ? ' cm-name-active' : ''}`}
            >
              {name}
            </span>
            <span className="cm-elo cm-mono">{elo}</span>
            {won ? (
              <span className="cm-crown" aria-label="Winner" title={`${name} won`}>
                <FontAwesomeIcon icon={faChessKing} />
              </span>
            ) : null}
          </span>
        );
        return (
          <div
            key={game.Id}
            className={`cm-glist-row${selectionMode || ready ? ' pointer' : ''}${selected ? ' cm-glist-selected' : ''}`}
            onClick={() => open(game)}
          >
            {selectionMode ? (
              <span className="cm-glist-select">
                <FontAwesomeIcon icon={selected ? faSquareCheck : faSquare} />
              </span>
            ) : null}
            <div className="cm-glist-when">
              <span className="cm-glist-day">{dayLabel(game.Date)}</span>
              <span className="cm-glist-time">{timeLabel(game.Date)}</span>
              {game.TimeControl ? <TimeControlPill label={game.TimeControl} /> : null}
            </div>
            <div className="cm-glist-main">
              <span className="cm-game-matchup">
                {side(game.White, game.WhiteElo, game.Result === '1-0')}
                <span className="cm-vs">vs</span>
                {side(game.Black, game.BlackElo, game.Result === '0-1')}
              </span>
              <span className="cm-glist-meta">
                {[game.Opening, Platform[game.Platform] === 'ChessCom' ? 'Chess.com' : 'Lichess']
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
            <div className="cm-glist-status">
              <GameStatus game={game} />
              {isAuthenticated ? (
                <button
                  type="button"
                  className={`cm-game-fav${starred ? ' active' : ''}`}
                  title={starred ? 'Starred' : 'Star this game'}
                  aria-pressed={starred}
                  onClick={(e) => {
                    e.stopPropagation();
                    setStarOverrides((o) => ({ ...o, [game.Id]: !starred }));
                    GamesAPI.updateUserGame(game.Id, !starred);
                  }}
                >
                  <FontAwesomeIcon icon={faStar} />
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      <GamesPager
        page={page}
        hasMore={games.HasMore}
        total={games.Total}
        count={games.List.length}
        onGoToPage={(p) => dispatch(setParams({ page: p }))}
      />
    </div>
  );
}

function GamesPager(props: {
  page: number;
  hasMore: boolean;
  total?: number;
  count: number;
  onGoToPage: (page: number) => void;
}): React.ReactNode {
  const { page, hasMore, total, count, onGoToPage } = props;
  const multiPage = page > 0 || hasMore;
  if (!multiPage && total == null) {
    return null;
  }
  const start = count > 0 ? page * PAGE_SIZE + 1 : 0;
  const end = page * PAGE_SIZE + count;
  return (
    <div className="cm-pager">
      {multiPage ? (
        <button
          type="button"
          className="cm-pager-btn"
          disabled={page <= 0}
          onClick={() => onGoToPage(page - 1)}
        >
          ← Prev
        </button>
      ) : null}
      <span className="cm-pager-status cm-mono">
        {total != null ? `${start}–${end} games of ${total}` : page + 1}
      </span>
      {multiPage ? (
        <button
          type="button"
          className="cm-pager-btn"
          disabled={!hasMore}
          onClick={() => onGoToPage(page + 1)}
        >
          Next →
        </button>
      ) : null}
    </div>
  );
}
